import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { Copy, Loader2, Mail, MessageCircle, XCircle } from "lucide-react";

import Modal from "src/components/ui/Modal";
import Input from "src/components/ui/Input";
import paymentApis from "src/api/payments";
import { currencyFormatter } from "src/utils/common";
import { useAppSelector } from "src/redux/hooks";
import { OrderPaymentSummary, PaymentLink } from "src/types/salePayment.types";
import { timeUntil, whatsappShareUrl } from "./paymentLabels";

const POLL_INTERVAL_MS = 4000;

interface PaymentLinkModalProps {
  open: boolean;
  link: PaymentLink;
  orderCode: string;
  customer?: { name?: string; mobile?: string; email?: string };
  onClose: () => void;
  // Fully paid through the link.
  onPaid: (amountReceived: number, order: OrderPaymentSummary | null) => void;
  // Part-paid / expired — refresh whatever is behind.
  onChanged?: () => void;
}

/**
 * Share a payment link: the link itself (copy), WhatsApp / email, and a QR
 * the customer can scan with their phone camera. Watches the link and
 * finishes on its own once it's paid.
 */
const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  open,
  link: initialLink,
  orderCode,
  customer,
  onClose,
  onPaid,
  onChanged,
}) => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenantName = user?.tenantDetails?.name;

  const [link, setLink] = useState<PaymentLink>(initialLink);
  const [emailing, setEmailing] = useState(false);
  const isOpen = ["active", "partially_paid"].includes(link.status);
  const remaining = Math.max(0, link.amount - link.amountPaid);

  useEffect(() => setLink(initialLink), [initialLink]);

  const refresh = useCallback(async () => {
    try {
      const res: any = await paymentApis.getPaymentLink(link._id);
      const next: PaymentLink = res?.data?.link;
      const order: OrderPaymentSummary | null = res?.data?.order || null;
      if (!next) return;
      if (next.amountPaid !== link.amountPaid || next.status !== link.status) onChanged?.();
      setLink(next);
      if (next.status === "paid" || (order && order.balanceDue <= 0)) {
        onPaid(next.amountPaid, order);
      }
    } catch {
      // Transient — the next tick retries.
    }
  }, [link, onChanged, onPaid]);

  useEffect(() => {
    if (!open || !isOpen) return;
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [open, isOpen, refresh]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const shareText = `Hi${customer?.name ? ` ${customer.name}` : ""}, please pay ${currencyFormatter(
    remaining
  )} for order ${orderCode}${tenantName ? ` at ${tenantName}` : ""}: ${link.shortUrl}`;

  const sendEmail = async () => {
    try {
      setEmailing(true);
      const res: any = await paymentApis.notifyPaymentLink(link._id, "email");
      toast.success(res?.message || `Link emailed to ${customer?.email}`);
    } catch (error: any) {
      toast.error(error.message || "Failed to email the link");
    } finally {
      setEmailing(false);
    }
  };

  const roundButton =
    "flex h-12 w-12 items-center justify-center rounded-full text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <Modal open={open} onClose={onClose} title={`Payment link — ${orderCode}`}>
      {!isOpen ? (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-200">
          <XCircle size={16} className="mt-0.5 shrink-0" />
          {link.status === "expired"
            ? "This payment link has expired — generate a new one from Collect payment."
            : "This payment link is no longer active."}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="text-center">
            <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {currencyFormatter(remaining)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Expires {timeUntil(link.expiresAt)}
              {link.amountPaid > 0 && ` · ${currencyFormatter(link.amountPaid)} received so far`}
            </div>
          </div>

          <Input
            value={link.shortUrl}
            disabled
            readOnly
            inputClassName="font-mono text-xs"
            endAdornment={
              <button
                type="button"
                onClick={copyLink}
                className="text-gray-500 hover:text-primary-600 dark:text-gray-400"
                title="Copy link"
              >
                <Copy size={16} />
              </button>
            }
          />

          <div className="flex justify-center gap-6">
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                className={`${roundButton} bg-green-500 hover:bg-green-600`}
                disabled={!customer?.mobile}
                title={customer?.mobile ? "Share on WhatsApp" : "Customer has no mobile number"}
                onClick={() =>
                  window.open(whatsappShareUrl(customer?.mobile, shareText), "_blank")
                }
              >
                <MessageCircle size={22} />
              </button>
              <span className="text-xs text-gray-600 dark:text-gray-400">WhatsApp</span>
            </div>
            {customer?.email && (
              <div className="flex flex-col items-center gap-1">
                <button
                  type="button"
                  className={`${roundButton} bg-blue-500 hover:bg-blue-600`}
                  disabled={emailing}
                  title={`Email to ${customer.email}`}
                  onClick={sendEmail}
                >
                  {emailing ? <Loader2 size={20} className="animate-spin" /> : <Mail size={22} />}
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400">Email</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700">
              <QRCodeSVG value={link.shortUrl} size={180} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customer can scan this with their phone camera to pay.
            </p>
          </div>

          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Loader2 size={13} className="animate-spin" />
            Waiting for payment — this updates automatically
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PaymentLinkModal;
